// @flow

import React, { useState } from 'react';
import { log, formatPhoneNumberForBackend } from '../../utils/jsUtils';
import { useDispatch } from 'react-redux';

import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import HeaderTopBar from '../HeaderTopBar';
import AppLoadingSpinner from '../SharedComponents/AppLoadingSpinner';

import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/StaffListHeader.Style';

import AddStaffDialog from './AddStaffDialog';

// Actions
import { setSnackMessage } from '../../reduxStore/modules/common/actions';

// RTK Query
import { useCreateStaffMutation } from '../../features/api/staffApi';

import { useI18n } from '../../contexts/I18nContext';

type Props = {
  classes?: Object,
  mobileOpen: bool,
  usehandleDrawerToggle: Function,
};


const StaffListHeader = (props: Props) => {
  log('[StaffListHeader] props: ', props);
  const {
    usehandleDrawerToggle
  } = props;

  const theme = useTheme();
  const classes = createClasses(styles, theme);

  const [openAddStaffDialog, setOpenAddStaffDialog] = useState(false);

  const dispatch = useDispatch();
  const i18n = useI18n();
  const [createStaff] = useCreateStaffMutation();

  /**
   *
   * @param {*} staff
   * @param {*} inviteChannel
   * @param {*} data input in form
   * @returns false if the action has failed: don't close modal
   */
  const handleAddStaff = (prereq: Function, successCallback: Function, failureCallback: Function) => async (data) => {
    if (typeof prereq !== 'undefined') {
      log('[handleAddStaff] running prereq...');
      prereq();
    }
    log('[handleAddStaff] data: ', data);

    const payload = {
      ...data,
      IsAdmin: false,
      IsManager: false,
      IsOwner: false
    };
    if (data.InviteChannel === 'sms') {
      const formattedNb = formatPhoneNumberForBackend(data.Phone);
      if (!formattedNb) {
        dispatch(setSnackMessage({
          message: 'Invalid phone number.',
          level: 'error',
          duration: 3000
        }));
        return false;
      }
      payload['Phone'] = formattedNb;
      delete payload.Email;
    } else {
      delete payload.Phone;
    }

    try {
      const result = await createStaff({ payload }).unwrap();
      log('[handleAddStaff.createStaff] result: ', result);
      const { message } = result;
      dispatch(setSnackMessage({
        message: message,
        level: 'success',
        duration: 1000000
      }));
      if (typeof successCallback !== 'undefined') {
        successCallback();
      }
      setOpenAddStaffDialog(false);
    } catch (error) {
      log('[handleAddStaff.create] error: ', error);
      if (typeof failureCallback !== 'undefined') {
        failureCallback();
      }
      dispatch(setSnackMessage({
        message: error.data || 'An error occured. Please try again',
        level: 'error',
        duration: 5000
      }));
    }
    return true;
  };


  return (
    <AppBar
      sx={classes.appBar}
      position="fixed"
    >
      <Toolbar
        sx={classes.toolbar}
        disableGutters
      >
        <Grid
          container
          direction='column'
        >
          <HeaderTopBar
            usehandleDrawerToggle={usehandleDrawerToggle}
          >
            <Typography
              sx={classes.pageTitle}
              variant='h2'>{i18n.t('Staff')}</Typography>
          </HeaderTopBar>
          <Grid
            item
            sx={{
              ...classes.rowSub,
              ...classes.bottomBar
            }}
          >
            <Grid container>
              <Box
                sx={{
                  ...classes.toggleContainer
                }}
              >
                <Tooltip
                  aria-label={`Add new ${i18n.t('staff')}`}
                  title={`Add new ${i18n.t('staff')}`}
                >
                  <Button
                    sx={classes.navButton}
                    onClick={() => setOpenAddStaffDialog(true)}
                  >
                    <i className={'icon-add'} />
                    <div>{i18n.t('Staff')}</div>
                  </Button>
                </Tooltip>
              </Box>

              <Box sx={classes.grow} />
            </Grid>
          </Grid>
          <AppLoadingSpinner />
        </Grid>
      </Toolbar>
      <AddStaffDialog
        open={openAddStaffDialog}
        setOpen={setOpenAddStaffDialog}
        triggerAction={handleAddStaff} />
    </AppBar>
  );
};

export default StaffListHeader;
